
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type { Client } from "@/types";

type SupabaseClient = {
  age: number;
  client_id: string;
  coach_id: string;
  created_at: string;
  full_name: string;
  gender: "male" | "female" | "other";
};

export function useClientsData() {
  const [searchQuery, setSearchQuery] = useState("");
  const [clients, setClients] = useState<Client[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();

  const ensureCoachExists = async (userId: string) => {
    try {
      // Check if coach record exists
      const { data: existingCoach, error: coachError } = await supabase
        .from('coach')
        .select('*')
        .eq('coach_id', userId)
        .single();

      if (coachError && coachError.code === 'PGRST116') {
        // Coach record doesn't exist, create it
        const { data: userData } = await supabase.auth.getUser();
        const userName = userData.user?.user_metadata?.full_name || 'Coach';
        
        const { error: createCoachError } = await supabase
          .from("coach")
          .insert({
            coach_id: userId,
            full_name: userName,
          });

        if (createCoachError) {
          console.error("Error creating coach record:", createCoachError);
          toast({
            title: "Error",
            description: "There was a problem setting up your coach profile.",
            variant: "destructive",
          });
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error("Error ensuring coach exists:", error);
      return false;
    }
  };

  const fetchClients = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (user) {
      // Ensure coach record exists before fetching clients
      const coachExists = await ensureCoachExists(user.id);
      if (!coachExists) {
        setIsLoading(false);
        return;
      }

      const { data: clientsData } = await supabase
        .from('clients')
        .select('*')
        .eq('coach_id', user.id);

      if (clientsData) {
        // Transform the Supabase data to match our Client type
        const transformedClients: Client[] = clientsData.map((client: SupabaseClient) => {
          return {
            id: client.client_id,
            name: client.full_name || '',
            avatar: '/placeholder.svg',
            joinDate: client.created_at,
            status: 'active',
            currentPlan: {
              name: 'Basic Training',
              progress: {
                current: 0,
                total: 100
              }
            },
            age: client.age,
            email: '',
            phone: '',
            progress: {
              completionRate: [],
              performance: []
            },
            notes: [],
            programs: [] // Add empty programs array to satisfy the type requirement
          };
        });
        setClients(transformedClients);
      }
    }
    setIsLoading(false);
  };

  // Add a function to remove a client from the local state
  const removeClient = (clientId: string) => {
    setClients(prevClients => prevClients.filter(client => client.id !== clientId));
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return {
    searchQuery,
    setSearchQuery,
    clients,
    filteredClients,
    isLoading,
    fetchClients,
    removeClient
  };
}
